import { ROUTES } from '@routes/routes';
import { Quill } from 'react-quill';
import { CustomImageValue, ImagePosition } from './interfaces';

const BlockEmbed = Quill.import('blots/block/embed') as any;

class CustomImageBlot extends BlockEmbed {
  static blotName = 'customImage';
  static tagName = 'figure';
  static className = 'ql-custom-image';

  static create(value: CustomImageValue) {
    const node = super.create();

    const container = document.createElement('div');
    container.className = 'ql-custom-image-container is-loading';

    const loader = document.createElement('div');
    loader.className = 'ql-custom-image-loader';
    loader.setAttribute('contenteditable', 'false');

    const img = document.createElement('img');
    img.setAttribute('data-image-id', value.imageId);
    img.setAttribute('draggable', 'false');
    img.alt = value.caption || '';
    img.onload = () => {
      container.classList.remove('is-loading');
      container.classList.add('is-loaded');
    };
    img.onerror = () => {
      container.classList.remove('is-loading');
      container.classList.add('is-error');
    };
    img.src = ROUTES.images.image(value.imageId);

    container.appendChild(loader);
    container.appendChild(img);

    const toolbar = document.createElement('div');
    toolbar.className = 'ql-custom-image-toolbar-container';
    toolbar.setAttribute('contenteditable', 'false');

    toolbar.innerHTML = `
      <div class="ql-custom-image-toolbar">
        <button type="button" data-action="edit-caption" class="icon-btn">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M3 10h11v2H3zm0-2h11V6H3zm0 8h7v-2H3zm15.01-3.13.71-.71c.39-.39 1.02-.39 1.41 0l.71.71c.39.39.39 1.02 0 1.41l-.71.71zm-.71.71-5.3 5.3V21h2.12l5.3-5.3z" />
          </svg>
        </button>

        <button type="button" data-action="size" class="icon-btn">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M21 15h2v2h-2zm0-4h2v2h-2zm2 8h-2v2c1 0 2-1 2-2M13 3h2v2h-2zm8 4h2v2h-2zm0-4v2h2c0-1-1-2-2-2M1 7h2v2H1zm16-4h2v2h-2zm0 16h2v2h-2zM3 3C2 3 1 4 1 5h2zm6 0h2v2H9zM5 3h2v2H5zm-4 8v8c0 1.1.9 2 2 2h12V11zm2 8 2.5-3.21 1.79 2.15 2.5-3.22L13 19z" />
          </svg>
        </button>

        <button type="button" data-action="position" class="icon-btn">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4m-7 7H3v4c0 1.1.9 2 2 2h4v-2H5zM5 5h4V3H5c-1.1 0-2 .9-2 2v4h2zm14-2h-4v2h4v4h2V5c0-1.1-.9-2-2-2m0 16h-4v2h4c1.1 0 2-.9 2-2v-4h-2z" />
            </svg>
        </button>

        <button type="button" data-action="wrap-text" class="icon-btn">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M4 19h6v-2H4zM20 5H4v2h16zm-3 6H4v2h13.25c1.1 0 2 .9 2 2s-.9 2-2 2H15v-2l-3 3 3 3v-2h2c2.21 0 4-1.79 4-4s-1.79-4-4-4" />
          </svg>
        </button>

        <button type="button" data-action="delete" class="icon-btn">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12 1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      </div>
  `;

    const captionEl = document.createElement('figcaption');
    captionEl.className = 'caption';
    captionEl.innerHTML = value.caption || '';

    container.appendChild(captionEl);
    container.appendChild(toolbar);

    node.setAttribute('data-position', value.position || 'center');
    node.setAttribute('data-wrap-text', value.wrapText || 'false');
    node.setAttribute('data-caption', value.caption || '');
    node.setAttribute('data-size', value.size || 50);
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('draggable', 'false');

    node.appendChild(container);

    CustomImageBlot.refreshToolbarButtons(node);

    return node;
  }

  static value(node: HTMLElement): CustomImageValue {
    const img = node.querySelector('img');

    return {
      imageId: img?.getAttribute('data-image-id') || '',
      caption: node.getAttribute('data-caption') || '',
      size: Number(node.getAttribute('data-size') || 50),
      position: (node.getAttribute('data-position') as any) || 'center',
      wrapText: node.getAttribute('data-wrap-text') === 'true',
    };
  }

  static refreshToolbarButtons(node: HTMLElement) {
    const buttons = node.querySelectorAll('button');

    buttons.forEach((button: HTMLButtonElement) => {
      button.classList.remove('disabled');
      button.removeAttribute('disabled');
    });

    const size = node.getAttribute('data-size');
    const position = node.getAttribute('data-position');

    const wrapTextButton = node.querySelector(
      'button[data-action="wrap-text"]'
    );
    const positionButton = node.querySelector('button[data-action="position"]');

    if (size === '100') {
      wrapTextButton?.classList.add('disabled');
      wrapTextButton?.setAttribute('disabled', 'true');

      positionButton?.classList.add('disabled');
      positionButton?.setAttribute('disabled', 'true');
    }

    if (position === 'center') {
      wrapTextButton?.classList.add('disabled');
      wrapTextButton?.setAttribute('disabled', 'true');
    }
  }

  updateCaption(caption: string) {
    this.domNode.setAttribute('data-caption', caption);

    const captionNode = this.domNode.querySelector('.caption');

    if (captionNode) {
      captionNode.innerText = caption;
    }
  }

  updatePosition(position: ImagePosition) {
    this.domNode.setAttribute('data-position', position);
    CustomImageBlot.refreshToolbarButtons(this.domNode);
  }

  updateSize(size: number) {
    this.domNode.setAttribute('data-size', size);
    CustomImageBlot.refreshToolbarButtons(this.domNode);
  }

  setEditMode(editMode: boolean) {
    if (editMode) {
      this.domNode.classList.add('is-editing');
    } else {
      this.domNode.classList.remove('is-editing');
    }
  }
}

export default CustomImageBlot;
